/*
 * iter-287 — My Companies page (simplified).
 *
 * Reads /api/companies/mine (union across cloudgm_business + asb606_admin).
 * Card now shows only a summary: company name, license #, legal form,
 * issue date, and expiry status pill. Full details, contact persons,
 * renewals, documents and license PDF download live on the detail
 * page /companies/:id (see CompanyDetail.js).
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ExternalLink, AlertCircle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Navbar } from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyCompanies() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('business_token');
        const res = await axios.get(`${BACKEND_URL}/api/companies/mine`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCompanies(res.data || []);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to load companies');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-GB', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    } catch { return iso; }
  };

  const daysUntil = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      const now = new Date();
      return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
    } catch { return null; }
  };

  return (
    <div className={`min-h-screen cgm-page`}>
      <Navbar onLogout={() => {
        // iter-302: navigate FIRST, then clear state — no white flash.
        navigate('/', { replace: true });
        localStorage.removeItem('business_token');
        localStorage.removeItem('business_user');
        window.dispatchEvent(new Event('userLoggedOut'));
      }} />
      <div className="flex">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <main className={`flex-1 min-w-0 transition-all duration-300 pb-10 ${sidebarOpen ? (language === 'ar' ? 'lg:mr-64' : 'lg:ml-64') : ''}`}>
          {/* Hero header */}
          <section className="cgm-hero py-6 sm:py-8 mb-6">
            <div className="cgm-hero-content max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="cgm-reveal">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-3 bg-white/15 backdrop-blur-md text-white border border-white/20">
                  <Building2 className="h-3 w-3" />
                  {language === 'ar' ? 'الشركات المرتبطة بحسابك' : 'Linked to your account'}
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="cgm-icon-glow cgm-breathe !p-2.5">
                    <Building2 className="h-5 w-5 text-white" />
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                    {language === 'ar' ? 'شركاتي' : 'My Companies'}
                  </h1>
                </div>
                <p className="text-white/80 text-sm sm:text-base">
                  {language === 'ar'
                    ? 'كل شركة مرتبطة بحسابك. اضغط "عرض الملف" لكل التفاصيل والوثائق.'
                    : 'Every company linked to your account. Tap "View file" for full details & documents.'}
                </p>
              </div>
            </div>
          </section>

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

            {loading && (
              <div className="cgm-ai-card" data-testid="my-companies-loading">
                <div className="animate-pulse space-y-3">
                  <div className="cgm-shimmer h-4 rounded" />
                  <div className="cgm-shimmer h-4 w-2/3 rounded" />
                </div>
              </div>
            )}

            {!loading && error && (
              <div className={`cgm-ai-card !border-red-300 ${isDarkMode ? '!bg-red-500/10' : '!bg-red-50'}`} data-testid="my-companies-error">
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="h-5 w-5" />
                  <span>{error}</span>
                </div>
              </div>
            )}

            {!loading && !error && companies.length === 0 && (
              <div className="cgm-ai-card text-center py-10" data-testid="my-companies-empty">
                <div className="cgm-icon-glow !w-16 !h-16 !p-4 inline-flex mb-4 cgm-float">
                  <Building2 className="h-8 w-8 text-white" />
                </div>
                <h3 className={`text-lg font-black mb-1 cgm-text-gradient-animated`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {language === 'ar' ? 'لا توجد شركات مرتبطة' : 'No linked companies yet'}
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                  {language === 'ar'
                    ? 'عندما تربط الإدارة ملف شركتك بحسابك، ستظهر هنا.'
                    : "When administration links your company file, it will appear here."}
                </p>
              </div>
            )}

            <div className="space-y-4 cgm-reveal-stagger">
              {companies.map((c) => {
                const daysLeft = daysUntil(c.expiry_date);
                const isExpiringSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 30;
                const isExpired = daysLeft !== null && daysLeft < 0;
                return (
                  <div
                    key={c.id}
                    className="cgm-ai-card-interactive !p-0 overflow-hidden group"
                    onClick={() => navigate(`/companies/${c.id}`)}
                    data-testid={`company-card-${c.id}`}
                  >
                    <div className="p-5">
                      {/* Title + expiry status */}
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="cgm-icon-glow !p-2 shrink-0 group-hover:scale-110 transition-transform duration-500">
                            <Building2 className="h-5 w-5 text-white" />
                          </div>
                          <h3 className={`text-lg font-black truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Cairo, sans-serif' }} data-testid={`company-name-${c.id}`}>
                            {language === 'ar' ? (c.company_name_ar || c.company_name_en) : (c.company_name_en || c.company_name_ar)}
                          </h3>
                        </div>
                        {isExpired && <span className="cgm-badge-danger shrink-0">{language === 'ar' ? 'منتهية' : 'Expired'}</span>}
                        {!isExpired && isExpiringSoon && <span className="cgm-badge-warning shrink-0">{language === 'ar' ? `${daysLeft} يوم` : `${daysLeft}d`}</span>}
                        {!isExpired && !isExpiringSoon && <span className="cgm-badge-success shrink-0">{language === 'ar' ? 'سارية' : 'Active'}</span>}
                      </div>

                      {/* Summary — 4 key fields only (user request iter-287) */}
                      <div className="grid grid-cols-2 gap-3 mb-5">
                        <Field label={language === 'ar' ? 'رقم الرخصة' : 'License #'} value={c.license_number} isDarkMode={isDarkMode} />
                        <Field label={language === 'ar' ? 'الشكل القانوني' : 'Legal form'} value={c.legal_form} isDarkMode={isDarkMode} />
                        <Field label={language === 'ar' ? 'تاريخ الإصدار' : 'Issue date'} value={fmtDate(c.issue_date)} isDarkMode={isDarkMode} />
                        <Field label={language === 'ar' ? 'تاريخ الانتهاء' : 'Expiry'} value={fmtDate(c.expiry_date)} isDarkMode={isDarkMode} />
                      </div>

                      {/* View-file button — navigates to /companies/:id */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/companies/${c.id}`); }}
                        className="cgm-cta-primary w-full"
                        data-testid={`view-company-file-${c.id}`}
                      >
                        <ExternalLink className="h-4 w-4" />
                        {language === 'ar' ? 'عرض الملف' : 'View file'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function Field({ label, value, isDarkMode }) {
  return (
    <div>
      <div className="cgm-eyebrow">{label}</div>
      <div className={`text-sm font-semibold truncate mt-0.5 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{value || '—'}</div>
    </div>
  );
}
